import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { OrganisationSettingsService } from '@/api';
import type { Organisation, UpdateOrganisationRequest } from '@/types/organisation';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';

export function useOrganisation() {
  const { orgId, isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: ['organisation', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('No organisation ID available');
      const response = await OrganisationSettingsService.getApiV1Organisations(orgId);
      return response as Organisation;
    },
    enabled: !!orgId && isSignedIn && isAuthReady,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: (failureCount, error) => {
      // Don't retry if it's a 404 (organisation not found) or 403 (access denied)
      if (error instanceof Error && (
        error.message.includes('404') || 
        error.message.includes('403')
      )) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

export function useUpdateOrganisation() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async (data: UpdateOrganisationRequest) => {
      if (!orgId) throw new Error('No organisation ID available');
      const response = await OrganisationSettingsService.putApiV1Organisations(orgId, data);
      
      return response as Organisation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisation', orgId] });
      // The effective settings carry the time tracking switch (#1903), which
      // the dashboard reads.
      queryClient.invalidateQueries({ queryKey: ['settings', 'effective'] });
      toast.success(t`Organisation updated successfully`);
    },
    onError: () => {
      toast.error(t`Failed to update organisation`);
    },
  });
}